import { FC, useState } from 'react';
import { createUseStyles } from 'react-jss';
import { IonButton, IonContent } from '@ionic/react';
import { useHistory } from 'react-router';
import { useInstance } from '@meraki-internal/react-dependency-injection';
import { InfoIcon } from '../theme/Icons';
import { Text } from '../components/Text';
import { InfoBanner } from '../components/InfoBanner';
import { Modal } from '../components/Modal';
import { useNudgeStorage } from '../utils/useNudgeStorage';
import { MixPanelEventEmitter } from '../metrics/MixPanelEventEmitter';
import { CareMapState } from '../caremap/CareMapState';

const useStyles = createUseStyles({
    section: {
        marginTop: 16,
        lineHeight: 1.5
    },
    openNotes: {
        marginTop: 24
    }
});

const TITLE = 'Posts are now shared notes';

// The community feed's posts became shared notes; this tells the people who had the feed where its
// posts went, until they dismiss it. With canManage it also says the page is now where access is
// managed; without, it speaks to someone the posts were shared with.
export const PostsMovedNotice: FC<{ careMapId: string; canManage: boolean; }> = ({ careMapId, canManage }) => {
    const history = useHistory();
    const mixpanel = useInstance(MixPanelEventEmitter);
    const careMapState = useInstance(CareMapState);
    // the two wordings are dismissed apart, for someone who manages one care map and reads another
    const [visible, dismiss] = useNudgeStorage({ nudgeId: canManage ? 'community-posts-moved-to-notes' : 'community-shared-posts-moved-to-notes' });
    const [learnMoreOpen, setLearnMoreOpen] = useState(false);

    if (!visible) {
        return null;
    }

    const role = careMapState.getPrincipalRole();

    const openNotes = (openedFrom: 'Card' | 'Learn More') => {
        mixpanel.track('Posts Moved Notice Notes Opened', () => ({ 'Opened From': openedFrom, 'Role': role }));
        setLearnMoreOpen(false);
        history.push(`/notes/${careMapId}`);
    };

    const openLearnMore = () => {
        mixpanel.track('Posts Moved Notice Learn More Opened', () => ({ 'Role': role }));
        setLearnMoreOpen(true);
    };

    const onDismiss = () => {
        mixpanel.track('Posts Moved Notice Dismissed', () => ({ 'Role': role }));
        dismiss();
    };

    return (
        <>
            <InfoBanner
                data-type="posts-moved-notice"
                Icon={InfoIcon}
                title={TITLE}
                message={canManage
                    ? 'Everything you shared here is in the Notes tab, and the people in your community still see it, react to it and comment on it there. This page is now where you manage who has access.'
                    : 'The posts shared with you are now shared notes in the Notes tab, where you can still react to them and comment on them.'
                }
                onDismiss={onDismiss}
            >
                <IonButton data-type="posts-moved-notice-open-notes" onClick={() => openNotes('Card')}>Open Notes</IonButton>
                <IonButton fill="clear" data-type="posts-moved-notice-learn-more" onClick={openLearnMore}>Learn more</IonButton>
            </InfoBanner>
            <PostsMovedLearnMore canManage={canManage} isOpen={learnMoreOpen} onClose={() => setLearnMoreOpen(false)} onOpenNotes={() => openNotes('Learn More')} />
        </>
    );
};

const PostsMovedLearnMore: FC<{ canManage: boolean; isOpen: boolean; onClose: () => void; onOpenNotes: () => void; }> = ({ canManage, isOpen, onClose, onOpenNotes }) => {
    const classes = useStyles();

    return (
        <Modal isOpen={isOpen} title={TITLE} closeHandler={onClose} data-type="posts-moved-learn-more">
            <IonContent className="ion-padding">
                {canManage ? <ManagerLearnMore /> : <ReaderLearnMore />}
                <IonButton className={classes.openNotes} expand="block" data-type="posts-moved-learn-more-open-notes" onClick={onOpenNotes}>Open Notes</IonButton>
            </IonContent>
        </Modal>
    );
};

const ManagerLearnMore: FC = () => {
    const classes = useStyles();

    return (
        <>
            <Text type="subtitle">Your community updates have moved to Notes</Text>
            <div className={classes.section}>
                <Text>A post was always a shared note, so we have put the two together. Nothing you or your community posted is gone: every update, reaction and comment is on the note in the Notes tab.</Text>
            </div>
            <div className={classes.section}>
                <Text><strong>To share an update:</strong> record one with your voice, or open the Notes tab and tap Add Note. Leave "Shared with Viewers" on and everyone with access sees it, just like posts.</Text>
            </div>
            <div className={classes.section}>
                <Text><strong>To react or comment:</strong> the reactions and the comment bar are at the bottom of the note, for you, your co-owners, viewers and contributors. Tap the paper plane count to open a list of who reacted and who was sent the note.</Text>
            </div>
            <div className={classes.section}>
                <Text><strong>To manage your community:</strong> that is this page now. Invite someone, change a person's access or remove them under Sharing & Access, and choose under Default Note & Recording Access whether new notes and recordings start shared.</Text>
            </div>
        </>
    );
};

const ReaderLearnMore: FC = () => {
    const classes = useStyles();

    return (
        <>
            <Text type="subtitle">The updates shared with you have moved to Notes</Text>
            <div className={classes.section}>
                <Text>A post was always a shared note, so we have put the two together. Nothing is gone: every update, reaction and comment is on the note in the Notes tab.</Text>
            </div>
            <div className={classes.section}>
                <Text><strong>To see updates:</strong> open the Notes tab. The notes and recordings shared with you are there.</Text>
            </div>
            <div className={classes.section}>
                <Text><strong>To react or comment:</strong> the reactions and the comment bar are at the bottom of the note, just as they were on posts.</Text>
            </div>
        </>
    );
};
